/**
 * 交通与地下空间事故 — 公开资料研究参考的内容层（SC-TRAFFIC-01）。
 *
 * 内容来自用户提供的 SC-TRAFFIC-UNDERGROUND-01 执行包：
 *   02_CONTENT/traffic-underground-content.json（6 个典型问题、30 项信息需求、8 类能力研究主题）
 *   03_EVIDENCE/evidence-cards.json（18 张条款/章节级依据卡）
 *   03_EVIDENCE/sources.json（12 个政府权威来源）
 *   03_EVIDENCE/cases.json（5 个分类案例）
 *   03_EVIDENCE/official-links.csv 与 02_CONTENT/issue-evidence-links.csv（逐问题的依据关系）
 *
 * 这一层不是：
 *   不是正式 Capability 定义或 Capability ID；不是设备清单或配备状态；
 *   不是属地预案、现场事实或行动授权；不创建 Mission、Scenario Pin 或版本。
 *
 * 安全边界（01_CODEX/CODEX_MASTER.md C 节）：
 *   不生成封路、停运、风机控制、断电、排水、人员进入路线或疏散命令；
 *   不把某次事故的距离、水位、温度或时序当作通用阈值；
 *   不据图像或传感器画面判定人员已救出、道路安全或结构稳定。
 */

/** 研究内容的性质标签。与森林、危化品场景使用同一套“公开研究参考”语义。 */
export const transportReferenceNature = 'PUBLIC_RESEARCH_REFERENCE_PENDING_SCENARIO_OWNER';

/** 页面固定提示，只出现一次。 */
export const transportSafetyNotice =
  '本内容为公开资料研究参考，不代表实时事件、属地预案、现场事实或行动授权。实际处置应由属地专业指挥体系与运营单位依据现场证据、适用法规和专业人员判断组织实施。';

export const transportMediaDisclaimer = '以下画面为AI生成示意图，非真实事故现场或特定设备实拍。';

/* ------------------------------------------------------------------ 来源 */

export interface TransportSource {
  readonly id: string;
  readonly title: string;
  readonly org: string;
  readonly kind: string;
  readonly code: string;
  readonly published: string;
  /** 版本状态：现行、废止关系或待复核事项。必须与摘要分开显示。 */
  readonly status: string;
  /** 适用边界。 */
  readonly scope: string;
  readonly url: string;
  readonly documentUrl?: string;
  readonly documentLabel?: string;
}

export const transportSources: readonly TransportSource[] = [
  {
    id: 'MOT24-DRILL',
    title: '城市轨道交通运营突发事件应急演练管理办法',
    org: '交通运输部',
    kind: '规章／行政规范性文件',
    code: '交运规〔2024〕5号',
    published: '2024-08-07',
    status: '2024 年修订版本；2019 年版本同时废止',
    scope: '城市轨道交通运营突发事件的应急演练管理；不能直接套用到公路隧道。',
    url: 'https://xxgk.mot.gov.cn/xzgfxwj/202408/t20240807_4147491.html',
    documentUrl: 'https://www.gov.cn/gongbao/2024/issue_11566/material/gwygb202425.pdf',
    documentLabel: '官方公报 PDF（链接直达官方，未随包收录原件）',
  },
  {
    id: 'MOT18-MANAGE',
    title: '城市轨道交通运营管理规定',
    org: '交通运输部',
    kind: '规章',
    code: '交通运输部令2018年第8号',
    published: '2018-05-21',
    status: '官方刊载版本，2018-07-01 实施；执行前复核修订状态',
    scope: '地铁、轻轨等城市轨道交通运营和管理，不可直接作为道路交通具体操作规程。',
    url: 'https://xxgk.mot.gov.cn/gz/202112/t20211224_3632837.html',
  },
  {
    id: 'MOT24-RISK',
    title: '城市轨道交通运营安全风险分级管控和隐患排查治理管理办法',
    org: '交通运输部',
    kind: '管理办法',
    code: '交运规〔2024〕10号',
    published: '2024-11-15',
    status: '2024 年修订版本；2019 年版本同时废止',
    scope: '城市轨道交通运营风险和隐患管理，不等于事件发生时已经识别的实时风险。',
    url: 'https://xxgk.mot.gov.cn/xzgfxwj/202411/t20241115_4159469.html',
    documentUrl: 'https://xxgk.mot.gov.cn/2020/xzgfxwj/202411/W020241115382095966113.pdf',
    documentLabel: '官方 PDF（链接直达官方，未随包收录原件）',
  },
  {
    id: 'MOT23-ROAD',
    title: '公路运营领域重大事故隐患判定标准',
    org: '交通运输部',
    kind: '行业规范性文件',
    code: '交办公路〔2023〕59号',
    published: '2023-10-08',
    status: '2023 年印发；执行前核对有无新版',
    scope: '在役公路桥梁、隧道、重点路段及有关违法行为重大隐患判定，不是事故应急行动的自动指令。',
    url: 'https://app.www.gov.cn/govdata/gov/202310/14/508209/article.html',
  },
  {
    id: 'MOT26-TUNNEL',
    title: '公路隧道养护技术规范（JTG 5130—2026）发布公告',
    org: '交通运输部',
    kind: '强制性行业标准发布公告',
    code: '交通运输部公告2026年第42号',
    published: '2026-07-14',
    status: '2026-09-01 实施；原 JTG H12—2015 同时废止',
    scope: '公路隧道养护技术规范，非城市轨道交通隧道或地下建筑全部设施通用。',
    url: 'https://xxgk.mot.gov.cn/jigou/glj/202607/t20260714_4209592.html',
    documentUrl: 'https://xxgk.mot.gov.cn/jigou/glj/202607/P020260714390832074827.pdf',
    documentLabel: '发布公告 PDF（链接直达官方，未随包收录原件）',
  },
  {
    id: 'MOT26-PLAN',
    title: '交通运输部修订印发 5 项应急预案（含《公路交通突发事件应急预案》）',
    org: '交通运输部',
    kind: '最新应急预案发布信息',
    code: '交应急发〔2026〕5号（公开报道对应文件）',
    published: '2026-04-01',
    status: '2026 年修订；旧 2017 年五项预案同时废止（以正式发布文本为准）',
    scope: '说明预案体系及适用范围的更新，不等于已经取得具体附录处置细则。',
    url: 'https://www.mot.gov.cn/xinwen/jiaotongyaowen/202604/t20260401_4202857.html',
  },
  {
    id: 'MOHURD22-FIRE',
    title: '建筑防火通用规范 GB 55037—2022 发布公告',
    org: '住房和城乡建设部',
    kind: '强制性工程建设规范发布公告',
    code: '住建部公告2022年第189号',
    published: '2023-01-19',
    status: '2023-06-01 实施；与现行标准冲突以本规范为准',
    scope: '地下建筑等工程建设防火通用规范；不能据公告推导具体构造的现场状态。',
    url: 'https://www.mohurd.gov.cn/gongkai/zc/wjk/art/2023/art_17339_770016.html',
  },
  {
    id: 'BJ24-REPORT',
    title: '北京地铁昌平线“12·14”列车追尾事故调查报告',
    org: '北京市政府事故调查组／北京市应急管理局',
    kind: '事故调查报告',
    code: '北京地铁昌平线事故报告',
    published: '2024-02-05',
    status: '官方 PDF 31 页；本页只提供官方链接，未随包收录原件',
    scope: '发生在地铁昌平线的列车追尾事故；事发区段为地面／高架交通，不能称为地下隧道事故。',
    url: 'https://yjglj.beijing.gov.cn/art/2024/2/5/art_4520_652.html',
    documentUrl:
      'https://yjglj.beijing.gov.cn/attach/0/%E5%8C%97%E4%BA%AC%E5%9C%B0%E9%93%81%E6%98%8C%E5%B9%B3%E7%BA%BF%E2%80%9C12%C2%B714%E2%80%9D%E5%88%97%E8%BD%A6%E8%BF%BD%E5%B0%BE%E4%BA%8B%E6%95%85%E8%B0%83%E6%9F%A5%E6%8A%A5%E5%91%8A.pdf',
    documentLabel: '官方调查报告 PDF（链接直达官方，未随包收录原件）',
  },
  {
    id: 'ZZ22-REPORT',
    title: '河南郑州“7·20”特大暴雨灾害调查报告',
    org: '国务院调查组／应急管理部',
    kind: '灾害调查报告',
    code: '郑州7·20调查报告',
    published: '2022-01-21',
    status: '官方下载端点在部分网络可能返回 405，保留官方新闻与答记者问备用',
    scope: '包括郑州地铁 5 号线和京广快速路北隧道两起不同的亡人事件，必须分别标明。',
    url: 'https://www.mem.gov.cn/xw/bndt/202201/t20220121_407106.shtml',
    documentUrl: 'https://xxgk.mem.gov.cn/ezweb/ctrl/news/download?fileId=1825175337991610',
    documentLabel: '官方报告下载（链接直达官方，未随包收录原件）',
  },
  {
    id: 'ZZ22-QA',
    title: '国务院调查组就郑州“7·20”调查答记者问',
    org: '应急管理部',
    kind: '官方调查解读',
    code: '官方答记者问',
    published: '2022-01-21',
    status: '官方原文公开可读',
    scope: '分别说明地铁 5 号线及京广北隧道人员伤亡事件的认定和相关问题。',
    url: 'https://www.mem.gov.cn/xw/bndt/202201/t20220121_407135.shtml',
  },
  {
    id: 'JINJI14-REPORT',
    title: '晋济高速岩后隧道“3·1”特别重大道路交通危化品燃爆事故调查报告',
    org: '国务院事故调查组／原国家安全监管总局',
    kind: '事故调查报告（官方全文网页）',
    code: '晋济高速岩后隧道报告',
    published: '2014-06-10',
    status: '官方报告正文 HTML；不冒称存在已下载的官方 PDF',
    scope: '公路隧道内危险货物运输车辆事故，涉及危化品，与单纯车辆事故不可等同。',
    url: 'https://www.mem.gov.cn/gk/sgcc/tbzdsgdcbg/2014/201406/t20140610_245221.shtml',
  },
  {
    id: 'MEIDA25-REPORT',
    title: '梅大高速茶阳路段“5·1”塌方灾害调查评估报告',
    org: '广东省政府调查评估组（盐田区应急管理局提供副本）',
    kind: '灾害调查评估报告',
    code: '梅大高速塌方调查',
    published: '2025-04-21',
    status: '政府官网提供 31 页 PDF；本页提供官方链接，未随包收录原件',
    scope: '地表高速路堤塌方，不是地下隧道或地铁事故；仅在交通结构风险研究中作关联案例。',
    url: 'https://www.yantian.gov.cn/ytaqscddglj/gkmlpt/content/12/12136/mpost_12136629.html',
    documentUrl: 'https://www.yantian.gov.cn/ytaqscddglj/attachment/1/1572/1572633/12136629.pdf',
    documentLabel: '调查评估报告 PDF（链接直达官方，未随包收录原件）',
  },
] as const;

export function findTransportSource(id: string): TransportSource | undefined {
  return transportSources.find((row) => row.id === id);
}

/* ------------------------------------------------------------------ 依据卡 */

export type TransportEvidenceCategory =
  '法规条款' | '标准发布公告' | '官方发布说明' | '真实事故案例' | '相关道路案例';

export const transportEvidenceCategoryOrder: readonly TransportEvidenceCategory[] = [
  '法规条款',
  '标准发布公告',
  '官方发布说明',
  '真实事故案例',
  '相关道路案例',
];

export interface TransportEvidence {
  readonly id: string;
  readonly sourceId: string;
  /** 官方文件中的准确条款或报告章节。 */
  readonly locator: string;
  readonly label: string;
  /** 这条依据说明什么（业务语言）。 */
  readonly plain: string;
  readonly why: string;
  /** 不能从中推导什么。 */
  readonly cannot: string;
  readonly issueIds: readonly string[];
  readonly category: TransportEvidenceCategory;
  /** 确有适用案例时才有值；没有关系就留空，不强行附会。 */
  readonly caseIds: readonly string[];
}

export const transportEvidence: readonly TransportEvidence[] = [
  {
    id: 'E01',
    sourceId: 'MOT24-DRILL',
    locator: '第七条第（六）项',
    label: '轨道列车、车站和区间火灾的专项准备',
    plain:
      '轨道交通运营单位的专项应急预案及演练内容应覆盖列车、车站公共区、区间和设备房等区域的火灾。',
    why: '因此场景要组织起火区域、烟气观察、火灾报警及相关设施状态等信息需求。',
    cannot: '条款列举应覆盖的事故类型，不能据此直接命令开启某组风机或确定疏散路线。',
    issueIds: ['I01'],
    category: '法规条款',
    caseIds: [],
  },
  {
    id: 'E02',
    sourceId: 'MOT18-MANAGE',
    locator: '第四十三条、第四十四条',
    label: '站车警示、报警设备与乘客疏散信息',
    plain:
      '运营单位应设置安全提示及必要设备，突发事件后按岗位职责通过广播、乘客信息系统和人工引导等方式组织疏散。',
    why: '支撑“广播和出口状态、乘客位置、运营单位报告与通行条件”的信息需求。',
    cannot: '不能把这些通用职责当作本次事故已经采取了措施或已经存在可用疏散通路。',
    issueIds: ['I01', 'I03', 'I06'],
    category: '法规条款',
    caseIds: [],
  },
  {
    id: 'E03',
    sourceId: 'MOT24-DRILL',
    locator: '第七条第（一）项、第八条第（一）／（四）项',
    label: '列车撞击与区间阻塞',
    plain:
      '列车脱轨、撞击、冲突、挤岔，以及行车调度、列车故障、区间阻塞和乘客疏散均被列为应急演练重点。',
    why: '支撑列车车次、所在区间、车辆状态、调度记录及受影响人员信息的组织。',
    cannot: '没有给出任何实际列车故障诊断结果或当前运营指令。',
    issueIds: ['I02', 'I03'],
    category: '法规条款',
    caseIds: [],
  },
  {
    id: 'E04',
    sourceId: 'MOT24-RISK',
    locator: '第七条、第八条',
    label: '轨道风险点位应落实到具体场所与设施',
    plain:
      '风险按设施、设备、行车、客运和运行环境等板块组织；运营单位的风险数据库应将风险点定位到具体场所和设施设备部位。',
    why: '支撑“位置、设施名称、运行状态、风险责任来源”的信息项。',
    cannot: '风险数据库的要求不代表任何具体车站已建成或现时信息完整。',
    issueIds: ['I02', 'I05', 'I06'],
    category: '法规条款',
    caseIds: [],
  },
  {
    id: 'E05',
    sourceId: 'MOT24-DRILL',
    locator: '第七条第（三）／（四）项、第八条第（二）／（三）／（六）项',
    label: '轨道淹水倒灌及供电、信号、通信异常',
    plain:
      '应急演练应覆盖淹水倒灌、供电中断、通信中断和信号故障，并规定相关岗位在对应情形下的演练准备。',
    why: '支撑积水位置和水位、相关设施状态及岗位报告的待核实信息。',
    cannot: '不构成对特定设备直接断电、启动排水或运营恢复的自动授权。',
    issueIds: ['I04', 'I06'],
    category: '法规条款',
    caseIds: [],
  },
  {
    id: 'E06',
    sourceId: 'MOT18-MANAGE',
    locator: '第四十条至第四十六条',
    label: '运营单位的预案、装备、报告和跨部门职责',
    plain: '规定运营突发事件处置机制、应急物资与队伍、演练、现场引导、客流管理和事故报送等要求。',
    why: '支撑人员数量、现场状态、部门信息和授权来源的交叉核对。',
    cannot: '不能由研究页面替代属地应急预案、运营单位实时调度或政府统一指挥。',
    issueIds: ['I02', 'I03', 'I04', 'I06'],
    category: '法规条款',
    caseIds: [],
  },
  {
    id: 'E07',
    sourceId: 'MOT24-DRILL',
    locator: '第五条、第六条、第九条、第十三条',
    label: '轨道应急预案需衔接政府部门与信息协同',
    plain:
      '运营单位预案需衔接政府层面预案，相关主管部门联合消防、公安、卫生健康、应急管理等开展联动演练；文件鼓励提升信息获取传递效率。',
    why: '支撑协同通讯、部门责任、事件时间线与官方通告等内容。',
    cannot: '条款不意味着平台具备跨部门数据读权或自动调度权限。',
    issueIds: ['I03', 'I06'],
    category: '法规条款',
    caseIds: [],
  },
  {
    id: 'E08',
    sourceId: 'MOT23-ROAD',
    locator: '第四条、第六条、第八条',
    label: '公路隧道结构与有关危险交通行为',
    plain:
      '文件将在役公路隧道列为重大隐患判定对象；列出技术状况较差且未整治仍通行的隧道，以及特定危险货物车辆违反交通管制的情况。',
    why: '支撑隧道结构评估、封闭状态、运营养护与危险货物信息的核对。',
    cannot: '仅适用于规定的公路运营隐患类别，不应自动认定某隧道当前属于重大隐患。',
    issueIds: ['I02', 'I05'],
    category: '法规条款',
    caseIds: [],
  },
  {
    id: 'E09',
    sourceId: 'MOT26-TUNNEL',
    locator: '发布公告（实施日期与废止关系）',
    label: '公路隧道养护新标准',
    plain: 'JTG 5130—2026 于 2026 年 9 月 1 日实施，取代 JTG H12—2015。',
    why: '建议将目前的养护数据、结构检查和设施管理归入现行标准入口。',
    cannot: '仅根据公告无法推断其未逐条核对的具体监测指标，更不能引用已废止旧版为现行。',
    issueIds: ['I01', 'I05'],
    category: '标准发布公告',
    caseIds: [],
  },
  {
    id: 'E10',
    sourceId: 'MOHURD22-FIRE',
    locator: '公告中关于强制性规范与替代关系的说明',
    label: '地下建筑防火资料的制度基础',
    plain: 'GB 55037—2022 自 2023 年 6 月 1 日起实施，公告说明其全部条文为强制性工程建设规范。',
    why: '作为地下商业、地下车库等建筑防火资料的官方规范入口。',
    cannot: '公告自身不提供该场所的火灾设施实际状态，也不替代轨道或隧道专门标准。',
    issueIds: ['I01', 'I03'],
    category: '标准发布公告',
    caseIds: [],
  },
  {
    id: 'E11',
    sourceId: 'MOT26-PLAN',
    locator: '2026 年五项应急预案修订说明',
    label: '公路交通应急预案在 2026 年更新',
    plain: '交通运输部发布的 2026 年修订消息说明五项预案更新，强调事前预防、指挥组织和部门衔接。',
    why: '建议场景中的公路应急资料优先关联新修订预案入口，保留地方版本待确认。',
    cannot: '新闻摘要不是五份预案的全文，不能据此推断未公开的具体流程。',
    issueIds: ['I02', 'I05', 'I06'],
    category: '官方发布说明',
    caseIds: [],
  },
  {
    id: 'E12',
    sourceId: 'MOT24-RISK',
    locator: '第十二条、第二十条、第二十一条',
    label: '隐患识别、风险更新与信息共享',
    plain: '明确隐患管理、风险数据库更新和依托信息化系统进行相关信息共享的要求。',
    why: '支持长期风险监测与事件后的能力改进内容，不代表事故中实时监测已接入。',
    cannot: '不得由“共享要求”推定所有运营单位数据可被平台直接读取。',
    issueIds: ['I04', 'I05', 'I06'],
    category: '法规条款',
    caseIds: [],
  },
  {
    id: 'E13',
    sourceId: 'JINJI14-REPORT',
    locator: '事故报告“二、事故经过”“四、事故原因和性质”',
    label: '隧道车辆追尾可与其他风险叠加',
    plain: '报告调查了道路隧道内运输车辆追尾，随后出现危险货物泄漏、燃烧与其他车辆连锁影响。',
    why: '说明隧道车辆状态、载货信息、排队与交通阻塞信息需要同步核对。',
    cannot: '该事故涉及甲醇等危险货物，不能将其机制套用至一般隧道车辆碰撞。',
    issueIds: ['I01', 'I02'],
    category: '真实事故案例',
    caseIds: ['C03'],
  },
  {
    id: 'E14',
    sourceId: 'ZZ22-QA',
    locator: '第五问：郑州地铁 5 号线亡人事件',
    label: '轨道积水倒灌与调度信息的重要性',
    plain: '调查组说明暴雨内涝导致地铁隧道涌水，并认定有关方面在工程和应对处置中存在问题。',
    why: '支持对积水来源、水位、列车所在区间、运营调度和人员位置作结构化整理。',
    cannot: '不能将郑州事件中的具体时序、设施缺陷或责任认定套用于其他站点。',
    issueIds: ['I03', 'I04', 'I06'],
    category: '真实事故案例',
    caseIds: ['C01'],
  },
  {
    id: 'E15',
    sourceId: 'ZZ22-QA',
    locator: '第五问：京广快速路北隧道亡人事件',
    label: '道路隧道积水与封闭交通的核实',
    plain: '调查组对京广快速路北隧道亡人事件说明，隧道封闭和交通疏导不及时是调查认定的突出问题。',
    why: '支持通行管制生效时间、交通信息发布、车辆与人员位置等需求。',
    cannot: '不能直接以本事件替代当地交通管制批准流程或通用水深阈值。',
    issueIds: ['I02', 'I04', 'I06'],
    category: '真实事故案例',
    caseIds: ['C02'],
  },
  {
    id: 'E16',
    sourceId: 'BJ24-REPORT',
    locator: '第二章“事故经过及应急处置情况”、第六章与第七章；PDF 物理约第 12–17 页和第 26–30 页',
    label: '列车事故要串起行车、调度、救援全过程',
    plain:
      '事故调查报告复盘列车运行和调度信息、事故应急处置过程，并就信息通报、协同和演练提出意见。',
    why: '支持车次、区间、调度命令、受影响人员及救援衔接等信息组织。',
    cannot: '昌平线事故不是地下隧道事故，只能作为轨道运营关联案例。',
    issueIds: ['I02', 'I03', 'I06'],
    category: '真实事故案例',
    caseIds: ['C04'],
  },
  {
    id: 'E17',
    sourceId: 'MEIDA25-REPORT',
    locator: '调查评估报告中“主要原因”“主要教训”“风险监测管控”部分',
    label: '交通结构风险需要养护与监测证据',
    plain:
      '报告认定长时间降水与地质、水文及路基等因素共同作用造成高速路堤塌方，提出交通设施风险监测和养护等教训。',
    why: '支持结构检查、降雨与养护记录、封闭状态和来源持续更新的需求。',
    cannot: '该事件属于地面高速路堤灾害，不是地下隧道事故。',
    issueIds: ['I05'],
    category: '相关道路案例',
    caseIds: ['C05'],
  },
  {
    id: 'E18',
    sourceId: 'MOT24-RISK',
    locator: '第七条第（一）至（五）项',
    label: '设施、设备、行车、客运与运行环境要分别记录',
    plain: '轨道风险分类覆盖隧道、车站、车辆、供电通信、行车、客流和环境等多个板块。',
    why: '支持把事故核心问题拆成设备、人员、道路空间、环境和调度多个信息维度。',
    cannot: '分类是研究组织方式，不能直接推定风险等级。',
    issueIds: ['I01', 'I02', 'I03', 'I04', 'I05', 'I06'],
    category: '法规条款',
    caseIds: [],
  },
] as const;

export function findTransportEvidence(id: string): TransportEvidence | undefined {
  return transportEvidence.find((row) => row.id === id);
}

/**
 * 逐问题的依据关系。
 *
 * 18 张依据卡说明的是“这份文件说了什么”；同一份文件在不同问题里支撑的具体信息不同，
 * 因此这里按“问题｜依据”存一句关系说明。内容来自执行包的 issue-evidence-links.csv。
 */
const relations: Readonly<Record<string, string>> = {
  'I01|E01':
    '条款把列车、车站公共区、区间与设备房的火灾列为准备内容，所以本问题先要组织起火区域与火灾报警。',
  'I01|E02':
    '疏散广播、乘客信息系统与人工引导属于运营单位岗位职责，对应广播与出口状态、通行条件的信息需求。',
  'I01|E09': '现行隧道养护标准是隧道结构与设施检查资料的入口，提示把养护数据归入现行版本核对。',
  'I01|E10':
    '地下商业、地下车库等地下建筑的防火资料以本规范为官方入口，用于区分“建筑防火”与“轨道运营”两类适用空间。',
  'I01|E13':
    '隧道内车辆事故可与载货、燃烧和阻塞叠加，说明车辆状态、载货信息与排队情况需要一起核对。',
  'I01|E18':
    '风险分类把设施、设备、行车、客运与环境分开，本问题的信息需求同样按维度拆分而不是合并成“隧道火灾”。',
  'I02|E03':
    '条文把列车撞击、区间阻塞与乘客疏散列为演练重点，支撑车次、区间、车辆状态与调度记录的组织。',
  'I02|E04':
    '风险点位要求落到具体场所与设施部位，支撑“位置、设施名称、运行状态、责任来源”这类信息项。',
  'I02|E08':
    '公路隧道结构与危险交通行为属于公路运营隐患判定事项，支撑隧道封闭状态与危险货物信息的核对。',
  'I02|E11': '2026 年修订的公路交通应急预案是公路侧应急资料的现行入口，地方版本仍需另行确认。',
  'I02|E13':
    '隧道追尾事故显示车辆停驶、载货泄漏与后车连锁可能同时发生，不能用单一的“交通事故”概括。',
  'I02|E16':
    '调查报告把行车、调度与救援过程连成一条线，支撑车次、区间、调度命令与受困人员的信息组织。',
  'I03|E02': '广播、乘客信息系统与人工引导对应站车疏散条件，支撑“哪些通道实际可用”的判断依据来源。',
  'I03|E03': '乘客疏散被列为演练重点，说明人员位置与疏散条件属于准备阶段就要明确的信息。',
  'I03|E06': '运营单位对报告、引导与客流管理承担责任，因此人数与位置必须回到责任部门的记录核对。',
  'I03|E07': '与消防、公安、卫生健康、应急管理的联动要求，支撑多部门人员线索与交接时间线的整理。',
  'I03|E14':
    '郑州地铁 5 号线事件的调查结论把列车所在区间、调度与人员状态放在一起，说明这些信息需要同一时间线核对。',
  'I03|E16': '昌平线报告记录了现场人员救援与多部门协同过程，支撑人员位置与转运衔接口径的统一。',
  'I04|E05':
    '条文把淹水倒灌、供电与通信中断列为演练情形，直接对应水情、设施状态与岗位报告三类信息。',
  'I04|E06': '运营单位的处置机制与报送要求，说明停运、隔离等措施由谁确认、由谁发布。',
  'I04|E12': '隐患识别与风险数据库更新要求，支撑把水情与设施状态作为持续记录而不是一次性快照。',
  'I04|E14': '地铁隧道涌水事件的调查结论，支撑对进水点、水位变化与区间位置作结构化整理。',
  'I04|E15':
    '京广快速路北隧道事件显示封闭与疏导是否及时是关键问题，支撑管制生效时间与发布来源的核对。',
  'I05|E04': '风险点位落到具体场所与设施设备部位，支撑结构异常位置与责任来源的信息项。',
  'I05|E08': '公路隧道技术状况与通行情况属于隐患判定对象，支撑结构评估与封闭状态的核对。',
  'I05|E09': '现行养护标准取代旧版，说明结构检查与养护记录要引用当前有效版本，旧版不得标为现行。',
  'I05|E11': '修订后的公路交通应急预案是公路侧应急资料入口，支撑运营状态信息来源的登记。',
  'I05|E12': '风险更新与信息化共享要求，支持养护与监测资料长期留痕，但不等同于实时监测已接入。',
  'I05|E17': '梅大高速塌方评估报告强调监测与养护教训，支撑结构检查、降雨记录与封闭状态的信息需求。',
  'I06|E05': '通信中断被列为演练情形，支撑通信链路状态与失联点位的信息需求。',
  'I06|E06': '运营单位报告与跨部门报送职责，支撑把部门来源与发布时间写清楚。',
  'I06|E07': '预案衔接与联动演练要求，支撑协同通讯与责任划分的整理，但不赋予平台调度权限。',
  'I06|E11': '应急预案更新强调指挥组织与部门衔接，提示公路侧资料应引用现行版本。',
  'I06|E12': '信息共享要求支撑多源记录的组织方式，不能据此推定平台可直接读取运营单位系统。',
  'I06|E14': '地铁 5 号线事件的调查结论涉及预警、调度与应对，支撑事件时间线的多源核对。',
  'I06|E16': '昌平线报告对信息通报与协同提出意见，支撑“同一状态是否存在不同来源版本”的核对。',
};

/** 这份依据在当前问题中支撑什么。取不到时退回依据卡自己的通用说明。 */
export function transportEvidenceRelation(issueId: string, evidenceId: string): string {
  return (
    relations[issueId + '|' + evidenceId] ??
    findTransportEvidence(evidenceId)?.why ??
    '本场景未登记这份资料与该问题之间的逐项关系。'
  );
}

/* ------------------------------------------------------------------ 案例 */

export interface TransportCase {
  readonly id: string;
  readonly title: string;
  readonly kind: string;
  readonly date: string;
  readonly location: string;
  readonly sourceIds: readonly string[];
  readonly section: string;
  readonly factual: string;
  readonly relevance: string;
  readonly limit: string;
  /** 适用范围标签：道路／轨道／地面或地下。防止把地面事件说成地下事故。 */
  readonly scopeLabel: string;
  readonly scopeNote: string;
}

export const transportCases: readonly TransportCase[] = [
  {
    id: 'C01',
    title: '郑州地铁 5 号线“7·20”亡人事件',
    kind: '官方灾害调查中的城市轨道交通事件',
    date: '2021-07-20',
    location: '河南郑州',
    sourceIds: ['ZZ22-REPORT', 'ZZ22-QA'],
    section:
      '《河南郑州“7·20”特大暴雨灾害调查报告》“郑州地铁 5 号线亡人事件”专题；应急管理部调查组答记者问第五问',
    factual:
      '极端暴雨引发城市内涝，地铁隧道进水；调查组对预警、列车运行调度、结构防护与应急处置等问题作出认定。',
    relevance: '地下空间积水倒灌、人员状态与信息报告、运营单位协同。',
    limit: '这是特定城市特定灾害的调查结论，不能直接用其水深、降雨或时序值推导全国统一运营阈值。',
    scopeLabel: '地铁区间 · 地下空间',
    scopeNote: '适用范围内的地下轨道事件；与京广快速路北隧道是两起不同事件，事实不合并。',
  },
  {
    id: 'C02',
    title: '郑州京广快速路北隧道“7·20”亡人事件',
    kind: '官方灾害调查中的城市道路隧道事件',
    date: '2021-07-20',
    location: '河南郑州',
    sourceIds: ['ZZ22-REPORT', 'ZZ22-QA'],
    section:
      '《河南郑州“7·20”特大暴雨灾害调查报告》“京广快速路北隧道亡人事件”专题；应急管理部答记者问第五问',
    factual:
      '极端暴雨期间京广快速路北隧道积水，调查组认定隧道封闭和交通疏导不及时是事故暴露的重要问题。',
    relevance: '道路隧道积水、通行管制状态核对、车辆及人员位置。',
    limit: '不能将特定事件的责任认定直接套用于所有地下通道。',
    scopeLabel: '城市道路隧道 · 地下空间',
    scopeNote: '与本场景的道路隧道问题直接对应；与地铁 5 号线事件分别记录。',
  },
  {
    id: 'C03',
    title: '晋济高速岩后隧道“3·1”事故',
    kind: '官方道路交通危险货物事故调查',
    date: '2014-03-01',
    location: '山西晋城',
    sourceIds: ['JINJI14-REPORT'],
    section: '调查报告“一、基本情况”“二、事故经过”“四、事故原因和性质”',
    factual: '公路隧道内两辆运输甲醇的货车发生追尾，出现甲醇泄漏、燃烧及其他车辆连锁影响。',
    relevance: '隧道车辆事故、交通拥堵、火灾烟气以及运输货物信息的重要性。',
    limit: '涉及危险化学品，具体化学危险性仍属危化品专业场景；不能对普通车辆火灾推导同样机制。',
    scopeLabel: '公路隧道 · 危化品运输',
    scopeNote:
      '轨道与道路两侧都不专属：燃爆机理属于危化品专业范围，本场景只引用其隧道车辆与阻塞信息。',
  },
  {
    id: 'C04',
    title: '北京地铁昌平线“12·14”列车追尾事故',
    kind: '官方城市轨道交通事故调查',
    date: '2023-12-14',
    location: '北京',
    sourceIds: ['BJ24-REPORT'],
    section:
      '调查报告第二章“事故经过及应急处置情况”、第六章“事故主要教训”、第七章“整改和防范措施建议”；PDF 物理第 12–17 页及第 26–30 页附近',
    factual:
      '报告记录列车追尾事故过程、调度信息、现场人员救援与多部门协同，并就极端天气下运营安全和信息传递提出改进意见。',
    relevance: '列车事故、行车状态与调度信息、人员救援协调。',
    limit: '该事故不是地下隧道事故，作为轨道运营关联案例展示，必须清楚标注。',
    scopeLabel: '地铁地面／高架区段 · 非地下事故',
    scopeNote: '轨道运营相关案例，但事发区段为地面／高架，不得称为地下隧道事故。',
  },
  {
    id: 'C05',
    title: '梅大高速茶阳路段“5·1”塌方灾害',
    kind: '官方道路结构灾害调查评估',
    date: '2024-05-01',
    location: '广东梅州',
    sourceIds: ['MEIDA25-REPORT'],
    section: '调查评估报告的灾害经过、主要原因、运营养护和监测预警方面的教训',
    factual:
      '长时间降雨与地质、水文和路基因素耦合作用造成高速公路路堤塌方，调查评估报告强调交通结构隐患和风险监测。',
    relevance: '交通结构受损、地质水文条件、养护与监测资料的重要性。',
    limit: '属于地面高速公路路堤灾害，非地下空间事故；仅作关联交通结构案例。',
    scopeLabel: '地面高速路堤 · 相关道路案例',
    scopeNote: '仅作为交通结构风险的相关案例，不属于地下空间或隧道事故。',
  },
] as const;

export function findTransportCase(id: string): TransportCase | undefined {
  return transportCases.find((row) => row.id === id);
}

/* ------------------------------------------------------------------ 能力研究主题 */

export interface TransportCapability {
  readonly id: string;
  readonly title: string;
  readonly image: string;
  readonly role: string;
  readonly inputs: readonly string[];
  readonly outputs: readonly string[];
  readonly limitation: string;
}

export const transportCapabilities: readonly TransportCapability[] = [
  {
    id: 'K01',
    title: '视频监测与线索核查',
    image: '01-metro-smoke.webp',
    role: '把摄像画面、设备告警、人工报告按区域和时间汇总，辅助定位仍需确认的位置。',
    inputs: ['视频／图像、告警记录', '观测时间与摄像机位置', '记录来源'],
    outputs: ['带时间与区域的观察画面', '疑点与待核实清单'],
    limitation: '无法凭单幅图像判定烟气成分、可通行性、人数或结构安全。',
  },
  {
    id: 'K02',
    title: '烟气与热异常观察',
    image: '06-thermal-view.webp',
    role: '利用已有烟雾探测、温度记录和可见光／热成像资料对比观察到的变化。',
    inputs: ['烟气传感／温度日志', '图像与时间标识', '设备配置和状态'],
    outputs: ['监测曲线、观测记录', '异常地点和时间线'],
    limitation: '热像不等于无烟视线，也不能判断安全呼吸空气；不自动控制排烟系统。',
  },
  {
    id: 'K03',
    title: '空间图纸与位置关联',
    image: '04-command-room.webp',
    role: '把站点、区间、道路里程、出入口、连接通道和设备位置关联到同一空间上下文。',
    inputs: ['经批准的线路／隧道图纸', '车站设施台账', '时间与空间坐标'],
    outputs: ['可检索空间关系、位置索引', '缺少的地图／资料清单'],
    limitation: '图纸可能与现实不一致；未经核实不能将地图上的路径标为“安全”。',
  },
  {
    id: 'K04',
    title: '远程机器人与受限区域侦察',
    image: '05-robot-tunnel.webp',
    role: '在符合设备适用条件的区域，通过遥控设备获取画面和位置线索，减少不必要的人员暴露。',
    inputs: ['远程设备摄像／传感画面', '通信状态与位置', '设备操作资格和适用条件'],
    outputs: ['区域观察记录、疑点位置', '设备任务日志（如已接入）'],
    limitation: '不得推定机器人适用于有爆炸、积水或带电风险的环境；设备能力须按型号验证。',
  },
  {
    id: 'K05',
    title: '人员位置与救援协同',
    image: '08-medical-transfer.webp',
    role: '整理人员线索、已确认受困与已转运状态，支持运营方、消防和医疗部门交接。',
    inputs: ['乘客／车辆人员报告', '进出站／车厢信息（依法取得）', '医疗交接与时序'],
    outputs: ['人员状态记录、未确认名单', '多部门交接时间线'],
    limitation: '人数与位置必须核实；不得用模型推测冒充已找到或已获救。',
  },
  {
    id: 'K06',
    title: '积水与结构风险监测',
    image: '03-underground-water.webp',
    role: '整理水位、渗漏、结构检查及设施状态的时间序列，辅助发现异常变化。',
    inputs: ['水位／渗漏监测', '结构巡检资料', '仪表来源和校准记录'],
    outputs: ['监测趋势与异常位置', '专业复核待办项'],
    limitation: '不能单靠水位读数或图像断定安全阈值、排水操作、封闭／复运决定。',
  },
  {
    id: 'K07',
    title: '应急通信与信息回传',
    image: '07-radio-relay.webp',
    role: '汇总对讲、广播、现场临时通信设备和指挥节点的可达信息，支持跨区域回传。',
    inputs: ['通信设备状态', '通讯覆盖与链路报告', '运营单位／指挥方信息'],
    outputs: ['链路状态与信息回传索引', '失联点位及待确认消息'],
    limitation: '展示通信能力不代表已部署、已获授权或可跨机构自动接通。',
  },
  {
    id: 'K08',
    title: '交通运行与管制状态汇集',
    image: '02-road-tunnel.webp',
    role: '将道路隧道封闭／交通导改信息与轨道停运／区间状态统一做来源登记供用户核查。',
    inputs: ['交通管理部门通告', '运营单位运行／封闭状态', '发布时间和影响区间'],
    outputs: ['可追溯交通状态卡', '信息发布与状态变化记录'],
    limitation: '只读显示权威已发布状态；不能自动关停信号、改交通灯或下达管制命令。',
  },
] as const;

export function findTransportCapability(id: string): TransportCapability | undefined {
  return transportCapabilities.find((row) => row.id === id);
}

/** 哪些问题登记了这个能力研究主题。 */
export function transportIssuesForCapability(id: string): readonly string[] {
  return transportIssues
    .filter((issue) => issue.capabilityIds.includes(id))
    .map((row) => row.title);
}

/* ------------------------------------------------------------------ 典型问题 */

export interface TransportInformationNeed {
  readonly name: string;
  /** 需要获取的内容。 */
  readonly what: string;
  /** 通常从哪里获得。 */
  readonly acquisition: string;
  /** 这一项对应的依据卡。 */
  readonly evidenceId: string;
}

export interface TransportIssue {
  readonly id: string;
  readonly key: string;
  readonly title: string;
  readonly context: string;
  readonly summary: string;
  readonly why: string;
  readonly questions: readonly string[];
  readonly information: readonly TransportInformationNeed[];
  readonly capabilityIds: readonly string[];
  readonly evidenceIds: readonly string[];
  readonly caseIds: readonly string[];
}

export const transportIssues: readonly TransportIssue[] = [
  {
    id: 'I01',
    key: 'fire-smoke',
    title: '隧道与地下空间火灾、烟气扩散',
    context: '道路隧道、地铁车站／区间、地下车库、地下通道等封闭或半封闭空间',
    summary:
      '火灾与烟气可能影响通行、能见度、疏散和设备运行。不同地下设施的通风、防火分隔和管理职责不同，必须先明确适用空间。',
    why: '可见火点不等于完整危险范围；烟气传播与通风条件需要结合设施图纸、报警记录和专业观察核对。',
    questions: [
      '事故属于道路隧道、地铁区间还是地下建筑？',
      '烟气在哪些通道或区域被观察到？',
      '报警、通风及防火设施的实际状态来自哪个来源？',
    ],
    information: [
      {
        name: '火灾位置与报警记录',
        what: '起始告警时间、报警分区、人工报告与摄像画面。',
        acquisition: '运营单位消防监控记录、值班报告、视频时间戳',
        evidenceId: 'E01',
      },
      {
        name: '地下空间与连通关系',
        what: '站厅、区间、道路洞室、出口、管井及相邻空间的连接情况。',
        acquisition: '正式设施平面图、运营单位图纸、场所管理资料',
        evidenceId: 'E10',
      },
      {
        name: '烟气与环境观察',
        what: '烟气出现位置、能见度与探测器读数的变化。',
        acquisition: '消防设备监测、视频、专业现场观察',
        evidenceId: 'E01',
      },
      {
        name: '通风与报警设备状态',
        what: '风机、排烟、报警、应急照明和其他设施的状态／故障记录。',
        acquisition: '相关运营或物业系统、维护日志',
        evidenceId: 'E18',
      },
      {
        name: '通行与人员信息',
        what: '可用出入口、疏散通道和受影响人员的已确认信息。',
        acquisition: '现场单位报告、客流／车辆信息、官方通告',
        evidenceId: 'E02',
      },
    ],
    capabilityIds: ['K01', 'K02', 'K03', 'K04', 'K07'],
    evidenceIds: ['E01', 'E02', 'E09', 'E10', 'E13', 'E18'],
    caseIds: ['C03'],
  },
  {
    id: 'I02',
    key: 'blockage',
    title: '列车或车辆撞击、事故阻塞',
    context: '地铁区间、站台及道路隧道内的车辆事故、拥堵和运营中断',
    summary:
      '列车或道路车辆事故可能导致车流阻塞、人员受伤、救援通道受限和次生事故。交通系统的实时运行事实与公众信息应分开记录。',
    why: '同一地点可能同时存在停驶、车辆受损、道路封闭、通信中断与受伤人员，不能把多个状态压缩为单一“交通事故”。',
    questions: [
      '事故属于轨道系统还是道路运输？',
      '实际停驶／封闭范围和时间是什么？',
      '车辆、人员及载货情况是否已经核实？',
    ],
    information: [
      {
        name: '事故位置与交通系统',
        what: '区间、里程桩号、进出口、道路方向或地铁线路车站。',
        acquisition: '官方通告、运营单位调度系统、地图与设施台账',
        evidenceId: 'E03',
      },
      {
        name: '车辆与运行状态',
        what: '车辆数量、车型、列车车次、行车方向、故障或停驶情况。',
        acquisition: '交通运营部门、车辆系统、事故报告',
        evidenceId: 'E03',
      },
      {
        name: '道路通行与阻塞状态',
        what: '通行方向、车流排队、已封闭路段及替代通道信息。',
        acquisition: '道路运营方和交通管理部门发布的状态',
        evidenceId: 'E15',
      },
      {
        name: '乘客及伤员线索',
        what: '已确认人数、伤情类型、人员所在区域及转运记录。',
        acquisition: '运营单位、卫生健康和现场联动部门报告',
        evidenceId: 'E16',
      },
      {
        name: '运输货物与次生危害',
        what: '运输车辆是否涉及危险货物、货物信息来源及待核实项。',
        acquisition: '运输单证和经核实的官方报告',
        evidenceId: 'E13',
      },
    ],
    capabilityIds: ['K01', 'K03', 'K04', 'K05', 'K08'],
    evidenceIds: ['E03', 'E04', 'E08', 'E11', 'E13', 'E16'],
    caseIds: ['C03', 'C04'],
  },
  {
    id: 'I03',
    key: 'people',
    title: '人员位置、受困及疏散条件不明',
    context: '车厢、站台、地下商用空间、道路隧道及连接通道',
    summary: '地下空间出口多、连通复杂，事故时人员位置、通道状态与广播指引需要被及时核实。',
    why: '被困与已疏散的人数可能在不同部门记录中不一致，未经复核不应直接给出完成率或安全结论。',
    questions: ['哪些区域仍可能有人？', '哪些通道和出口实际可用？', '哪些人员已被核实转运或转移？'],
    information: [
      {
        name: '人员数量与位置',
        what: '乘客、员工、驾驶员、受困和已转运人员的分区域记录。',
        acquisition: '运营单位、现场警消、医疗交接记录',
        evidenceId: 'E02',
      },
      {
        name: '出口与路线条件',
        what: '站厅／通道／道路出口的位置、是否阻塞和通行条件。',
        acquisition: '运营管理台账、现场观察、专业核查',
        evidenceId: 'E02',
      },
      {
        name: '通信与广播状态',
        what: '广播、乘客信息系统、紧急联络及现场指引记录。',
        acquisition: '车站系统、值班报告、官方告示',
        evidenceId: 'E06',
      },
      {
        name: '特殊人群与医疗衔接',
        what: '行动不便人员、受伤人员、交接与治疗去向。',
        acquisition: '卫健部门、运营单位、医疗转运记录',
        evidenceId: 'E16',
      },
      {
        name: '未确认事项清单',
        what: '来源矛盾、失联名单及最终确认责任。',
        acquisition: '多部门协同核实、调查报告',
        evidenceId: 'E07',
      },
    ],
    capabilityIds: ['K01', 'K03', 'K05', 'K07'],
    evidenceIds: ['E02', 'E03', 'E06', 'E07', 'E14', 'E16'],
    caseIds: ['C01', 'C04'],
  },
  {
    id: 'I04',
    key: 'water',
    title: '积水倒灌与供电、设施异常',
    context: '地铁区间、地下车站、城市下穿隧道、地下连通空间',
    summary:
      '地下空间进水可能与道路或轨道运营、设施供电、通信和疏散条件相互影响。研究页面首先需要掌握水情与设备状态。',
    why: '单个水位传感器不能代表全部区间；设备停止和隔离措施是否发生，应由运营单位和授权专业人员确认。',
    questions: [
      '进水来源和时间是否可追溯？',
      '哪些区间与设施受到影响？',
      '相关运营方已发布哪些正式状态？',
    ],
    information: [
      {
        name: '雨情、水情与变化时间',
        what: '天气预警、积水位置、连续监测值与时间。',
        acquisition: '气象部门、运营监测、现场记录',
        evidenceId: 'E05',
      },
      {
        name: '进水点与地下连通',
        what: '低洼入口、竖井、停车场等连接位置及实际进水观察。',
        acquisition: '设施图纸、运营资料、现场核查',
        evidenceId: 'E14',
      },
      {
        name: '供电、信号与机电状态',
        what: '供电、照明、通信、信号、泵房等设施状态。',
        acquisition: '运营系统、设备日志与责任人员报告',
        evidenceId: 'E05',
      },
      {
        name: '交通停运与管制',
        what: '官方封闭、停驶、清客或分流信息的时间及范围。',
        acquisition: '运营单位、交通管理部门发布记录',
        evidenceId: 'E15',
      },
      {
        name: '人员位置与外部救援衔接',
        what: '人员数量、位置、联络和转运状态。',
        acquisition: '消防、交通、卫生健康部门信息',
        evidenceId: 'E14',
      },
    ],
    capabilityIds: ['K01', 'K03', 'K05', 'K06', 'K07', 'K08'],
    evidenceIds: ['E05', 'E06', 'E12', 'E14', 'E15'],
    caseIds: ['C01', 'C02'],
  },
  {
    id: 'I05',
    key: 'structure',
    title: '结构受损与通行安全不明',
    context: '公路隧道衬砌／路面、轨道区间、地下结构、路基及相关交通设施',
    summary:
      '交通设施结构异常可能来自施工、长期养护问题、自然条件或事故损伤。资料需要保留具体位置、时间、检查结果和专业判断。',
    why: '外观图像和地形资料不能单独证明结构安全；“结构已正常”必须有专业检测和运营方依据。',
    questions: [
      '异常出现在哪里、由谁报告？',
      '是否存在可用的专业检查记录？',
      '管制及运营状态由哪个部门确认？',
    ],
    information: [
      {
        name: '结构异常位置与形式',
        what: '疑似裂缝、渗水、脱落、变形、路基异常等观察线索。',
        acquisition: '运营单位检测报告、现场影像、专业勘测',
        evidenceId: 'E04',
      },
      {
        name: '检查与养护记录',
        what: '最近检查日期、病害等级、维修和风险整改历史。',
        acquisition: '设施管理机构、养护记录',
        evidenceId: 'E09',
      },
      {
        name: '环境与外部影响',
        what: '降雨、地下水、周边施工等相关记录。',
        acquisition: '气象水文、施工许可、专业报告',
        evidenceId: 'E17',
      },
      {
        name: '道路或轨道通行状态',
        what: '已封闭区段、限制运行或恢复情况、发布时间。',
        acquisition: '交通管理部门和运营单位官方信息',
        evidenceId: 'E08',
      },
      {
        name: '专业安全评估状态',
        what: '结构是否经过专业评估、检测单位及结论有效范围。',
        acquisition: '有资质检测和专业工程评估',
        evidenceId: 'E08',
      },
    ],
    capabilityIds: ['K01', 'K03', 'K04', 'K06', 'K08'],
    evidenceIds: ['E04', 'E08', 'E09', 'E11', 'E12', 'E17'],
    caseIds: ['C05'],
  },
  {
    id: 'I06',
    key: 'comms',
    title: '通信中断与跨部门信息不一致',
    context: '道路隧道、地铁站区间、地下商业和多部门协同处置',
    summary:
      '地下空间可能存在通信覆盖弱、监控系统信息分散、不同部门记录时间不同等情况。需要将消息来源、时间与责任主体组织清楚。',
    why: '系统能显示某条消息，不代表它已由专业人员确认。跨域协同不等于平台具有调度或授权能力。',
    questions: [
      '哪些通信链路工作，哪些失联？',
      '同一状态是否存在不同来源版本？',
      '哪条信息已经由授权部门确认？',
    ],
    information: [
      {
        name: '通信设备与覆盖状况',
        what: '车地通信、对讲、广播、临时中继的状态与位置。',
        acquisition: '通信系统日志、设备运维、现场测试记录',
        evidenceId: 'E05',
      },
      {
        name: '部门报告与来源',
        what: '运营方、交警、消防、医疗、应急等的消息及时间。',
        acquisition: '原始部门通知与现场指挥记录',
        evidenceId: 'E07',
      },
      {
        name: '事件时间线',
        what: '告警、通报、人员转运、封闭状态的发生与记录时间。',
        acquisition: '调度日志、系统告警、官方信息发布',
        evidenceId: 'E16',
      },
      {
        name: '信息冲突和待核实项',
        what: '不同来源人数、位置和设施状态是否一致。',
        acquisition: '多源核实与负责人员签署记录',
        evidenceId: 'E06',
      },
      {
        name: '对外公布和授权范围',
        what: '已公开通告内容、发布单位和对应时间。',
        acquisition: '有权部门或运营单位正式通告',
        evidenceId: 'E06',
      },
    ],
    capabilityIds: ['K01', 'K03', 'K05', 'K07', 'K08'],
    evidenceIds: ['E05', 'E06', 'E07', 'E11', 'E12', 'E14', 'E16'],
    caseIds: ['C01', 'C04'],
  },
] as const;

export function findTransportIssue(key: string): TransportIssue | undefined {
  return transportIssues.find((row) => row.key === key || row.id === key);
}

export const defaultTransportIssue = 'fire-smoke';

/** 依据卡在“依据与案例”页里的分组标题下要说明自己到底是什么。 */
export const transportEvidenceGroupNote: Readonly<Record<TransportEvidenceCategory, string>> = {
  法规条款:
    '交通运输部规章与规范性文件的条款原文位置；条款说明应准备什么，不说明现场已经发生什么。',
  标准发布公告: '标准发布公告只说明版本、实施日期与替代关系；公告本身不含现场设施状态。',
  官方发布说明: '部门发布会与新闻说明的是预案体系更新，不是预案全文，也不含具体处置流程。',
  真实事故案例: '官方事故或灾害调查报告本身；每张卡片注明该事件的适用范围。',
  相关道路案例: '与交通结构风险相关的地面案例；不属于地下空间或隧道事故。',
};
